import { Box, Chip, CircularProgress, IconButton, Menu, MenuItem, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LinkIcon from "@mui/icons-material/Link";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { format } from "date-fns";
import { useState } from "react";

import type { StoryMapSummary } from "../../types/storyMap";
import { FROZEN_RIGHT, ROW_BG, frozenCellSx } from "./frozenColumns";
import { storyThumbnailUrl } from "./storyRows";

interface StoryRowProps {
  story: StoryMapSummary;
  /** What it draws from: the project's name, "<name> folder" or "All projects". */
  scopeLabel: string;
  showFolder: boolean;
  folderLabel: string;
  highlighted: boolean;
  viewOnly: boolean;
  rowRef: (el: HTMLTableRowElement | null) => void;
  onHoverChange: (hovering: boolean) => void;
  onOpen: () => void;
  onPreview: () => void;
  onCopyLink: () => void;
  onDelete: () => void;
  /** Fly the map to where the story sits; false when it has nowhere to go. */
  canLocate: boolean;
  onLocate: () => void;
}

export const THUMB_WIDTH = 56;
export const THUMB_HEIGHT = 34;

/** A story map in the listing's story-map browse mode. Click opens the builder. */
export default function StoryRow({
  story,
  scopeLabel,
  showFolder,
  folderLabel,
  highlighted,
  viewOnly,
  rowRef,
  onHoverChange,
  onOpen,
  onPreview,
  onCopyLink,
  onDelete,
  canLocate,
  onLocate,
}: StoryRowProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const thumb = storyThumbnailUrl(story, THUMB_WIDTH * 2, THUMB_HEIGHT * 2);
  const published = story.status === "PUBLISHED";
  const canShare = published && !!story.slug;

  return (
    <TableRow
      ref={rowRef}
      onClick={onOpen}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      sx={{
        cursor: "pointer",
        borderLeft: highlighted ? "3px solid #00d4ad" : "3px solid transparent",
        // Opaque colours: the frozen cells inherit them to cover scrolled content.
        backgroundColor: highlighted ? ROW_BG.highlightProject : ROW_BG.base,
        "&:hover": { backgroundColor: highlighted ? ROW_BG.highlightProjectHover : ROW_BG.hover },
        transition: "background-color 120ms ease",
      }}
    >
      <TableCell sx={{ minWidth: 240 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: THUMB_WIDTH,
              height: THUMB_HEIGHT,
              flex: "0 0 auto",
              borderRadius: 1,
              overflow: "hidden",
              backgroundColor: "#e2e8f0",
              backgroundImage: thumb ? `url("${thumb}")` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
            }}
          >
            {!thumb && <MapOutlinedIcon fontSize="small" />}
          </Box>
          <Typography noWrap sx={{ fontWeight: 600 }}>
            {story.title}
          </Typography>
        </Box>
      </TableCell>
      {showFolder && (
        <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>{folderLabel || "All projects"}</TableCell>
      )}
      <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>{scopeLabel}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <StatusChip story={story} />
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 600 }}>
        {story.scene_count}
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
        {story.updated_at ? format(new Date(story.updated_at), "MMM d, yyyy") : "—"}
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>{story.created_by_name ?? "—"}</TableCell>

      {/* Frozen controls, in the same columns as a folder row's: a story has
          no pin or eye, so those two cells stay empty. */}
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.pin)} />
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.eye)} />
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.locate)}>
        <Tooltip title={canLocate ? "Show on map" : "Nothing to show on the map yet"} placement="left">
          <span>
            <IconButton
              size="small"
              disabled={!canLocate}
              aria-label="Show on map"
              onClick={(e) => {
                e.stopPropagation();
                onLocate();
              }}
              sx={{ color: "#64748b", "&:hover": { color: "#00d4ad" } }}
            >
              <MyLocationIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell padding="none" align="right" sx={frozenCellSx(FROZEN_RIGHT.menu, true)}>
        <IconButton
          size="small"
          aria-label="Story map actions"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onOpen();
            }}
          >
            <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} /> Open
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onPreview();
            }}
          >
            <VisibilityOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Preview
          </MenuItem>
          {canShare && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onCopyLink();
              }}
            >
              <LinkIcon fontSize="small" sx={{ mr: 1 }} /> Copy share link
            </MenuItem>
          )}
          {!viewOnly && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onDelete();
              }}
            >
              <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
            </MenuItem>
          )}
        </Menu>
      </TableCell>
    </TableRow>
  );
}

/** Draft · Publishing… · Publish failed · Published (org) · Public. */
export function StatusChip({ story }: { story: StoryMapSummary }) {
  const small = { height: 20, fontSize: "0.7rem" } as const;
  switch (story.status) {
    case "PUBLISHING":
      return <Chip size="small" icon={<CircularProgress size={10} />} label="Publishing…" sx={small} />;
    case "PUBLISH_FAILED":
      return <Chip size="small" icon={<ErrorOutlineIcon />} label="Publish failed" color="error" sx={small} />;
    case "PUBLISHED":
      return story.visibility === "PUBLIC" ? (
        <Tooltip title="Anyone with the link can read it">
          <Chip size="small" label="Public" color="success" sx={small} />
        </Tooltip>
      ) : (
        <Tooltip title="Published to your organisation; readers sign in to see it">
          <Chip size="small" label="Published · org" color="success" variant="outlined" sx={small} />
        </Tooltip>
      );
    default:
      return <Chip size="small" label="Draft" sx={small} />;
  }
}
